<template>
  <aside class="w-full h-full flex flex-col gap-6 px-6 md:px-0">
    <!-- Chief Editorial -->
    <section class="bg-white/80 backdrop-blur-md p-6 shadow-xl rounded-2xl border border-gray-100 transition-all duration-300 hover:shadow-2xl gsap-section relative overflow-hidden group">
      <!-- Decorative background glow -->
      <div class="absolute -top-10 -right-10 w-32 h-32 bg-red-50 rounded-full blur-2xl opacity-50 z-0 pointer-events-none"></div>
      
      <div v-for="(section, index) in editorials" :key="index" class="mb-8 relative z-10 last:mb-0">
        <h2 class="text-xl font-bold mb-1 text-transparent bg-clip-text bg-gradient-to-r from-[#D84040] to-red-500">
          {{ section.title }}
        </h2>
        <h3 v-if="section.subtitle" class="mb-3">
          <span class="text-sm font-medium text-gray-500 tracking-wide uppercase">
            {{ section.subtitle }}
          </span>
        </h3>
        
        <div class="relative w-full h-52 mb-5 rounded-xl overflow-hidden shadow-sm group-hover:shadow-md transition-shadow">
          <img
            :src="require(`@/assets/images/${section.image_url}`)"
            :alt="section.title"
            class="w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
          />
          <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
        </div>
        
        <p class="text-gray-600 text-[15px] leading-relaxed mb-4">
          {{ truncateWords(section.description, 10) }}
        </p>
        
        <button
          @click="
            $router.push({
              name: 'EditorialDetail',
              params: { id: section.id },
            })
          "
          class="inline-flex items-center space-x-2 text-[#D84040] font-semibold text-sm hover:text-red-600 transition-colors group/btn"
        >
          <span>Read More</span>
          <svg class="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
        </button>
      </div>
    </section>

    <!-- Vision -->
    <section
      ref="vision"
      class="bg-white/80 backdrop-blur-md p-6 shadow-xl rounded-2xl border border-gray-100 transition-all duration-300 hover:shadow-2xl gsap-section relative overflow-hidden group mt-auto"
    >
      <div class="absolute -bottom-10 -left-10 w-32 h-32 bg-red-50 rounded-full blur-2xl opacity-50 z-0 pointer-events-none"></div>
      
      <div class="relative z-10 flex items-center mb-4 space-x-2">
        <h2 class="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D84040] to-red-500">
          Vision
        </h2>
        <span class="text-gray-400 font-medium text-sm">( দৃষ্টিভংগী )</span>
      </div>
      
      <div class="relative w-full h-48 mb-5 rounded-xl overflow-hidden shadow-sm group-hover:shadow-md transition-shadow">
        <img
          src="../assets/images/Vision.jpg"
          alt="Our Vision"
          class="w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
        />
        <div class="absolute inset-0 bg-[#D84040]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 mix-blend-overlay pointer-events-none"></div>
      </div>
      
      <p class="text-gray-600 text-[15px] leading-relaxed text-justify mb-5 relative z-10 font-medium">
        সংস্কৃতি মানুহৰ জীৱন প্ৰবাহ। ই ব্যক্তি নিৰ্ভৰ নহয়, সমাজ নিৰ্ভৰ। সমাজ মানেই জনসাধাৰণ। আৰু সেইবাবেই জনসাধৰণক সংস্কৃতিৰ বিভিন্ন দিশ প্ৰত্যক্ষ কৰোৱাৰ...
      </p>
      
      <router-link
        to="/vision"
        class="inline-flex w-full justify-end items-center space-x-2 text-[#D84040] font-semibold text-sm hover:text-red-600 transition-colors group/link relative z-10"
      >
        <span>Read More</span>
        <svg class="w-4 h-4 transform group-hover/link:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
      </router-link>
    </section>
  </aside>
</template>

<script>
import axios from "axios";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default {
  name: "EditorialSection",
  data() {
    return {
      editorials: [],
    };
  },
  methods: {
    truncateWords(text, wordLimit) {
      if (!text) return "";
      const words = text.split(" ");
      if (words.length <= wordLimit) return text;
      return words.slice(0, wordLimit).join(" ") + "...";
    },

    animateEditorials() {
      gsap.from(".gsap-card", {
        scrollTrigger: {
          trigger: ".gsap-card",
          start: "top 85%",
          toggleActions: "play none none none",
        },
        opacity: 0,
        y: 50,
        duration: 1,
        ease: "power2.out",
        stagger: 0.3,
      });

      // Optional: Slight zoom for images
      gsap.from(".gsap-card img", {
        scrollTrigger: ".gsap-card img",
        scale: 0.95,
        opacity: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: "power1.out",
      });
    },
  },

  mounted() {
    axios
      .get(`${process.env.VUE_APP_API_BASE_URL}/editorials`)
      .then((res) => {
        this.editorials = res.data;
        this.$nextTick(() => {
          this.animateEditorials();
        });
      })
      .catch((err) => {
        console.error("Failed to fetch editorials:", err);
      });
  },
};

</script>